// src/tenantAdmin/pages/TenantRunListPage.jsx
// "Storico Generazioni" (Brian UI). Dual-mounted at /runs (every member) and
// /tenant-admin/runs. Presentation is self-contained here — the shared admin
// components (AdminTable/RunFilters/AdminPagination) still back the SA console.
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageBand from '../../components/ui/PageBand.jsx';
import { FILTER_LABEL, FILTER_FIELD, FILTER_BUTTON, FilterDateField } from '../../components/ui/filterFields.jsx';
import { Eye, Mail, ChevronDown, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { listRuns } from '../../admin/runsApi.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import { COLORS, FONT_SANS, RADII, SEMANTIC, SHADOW } from '../../constants/theme.js';
import RunStatusPill from '../components/RunStatusPill.jsx';
import RunDetailModal from '../components/RunDetailModal.jsx';

const LIMIT = 10;

// Valid platform · content_format pairs (mirrors RunFilters/backendClient) — a single
// combined dropdown prevents impossible combos. `cf: ''` means platform-only.
const COMBO_OPTIONS = [
  { platform: 'linkedin',  cf: 'post',          label: 'LinkedIn · Post' },
  { platform: 'linkedin',  cf: 'carousel',      label: 'LinkedIn · Carousel' },
  { platform: 'linkedin',  cf: 'newsletter',    label: 'LinkedIn · Newsletter' },
  { platform: 'blog',      cf: 'seo',           label: 'Blog · SEO Article' },
  { platform: 'blog',      cf: 'case-study',    label: 'Blog · Case Study' },
  { platform: 'email',     cf: 'email-classic', label: 'Email · Classic' },
  { platform: 'landing',   cf: 'landing',       label: 'Landing Page' },
  { platform: 'instagram', cf: 'post',          label: 'Instagram · Post' },
  { platform: 'facebook',  cf: 'post',          label: 'Facebook · Post' },
  { platform: 'ads',       cf: '',              label: 'Ads' },
];
const comboKey = (platform, cf) => (platform ? `${platform}|${cf || ''}` : '');

function daysAgoIso(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

// Preset ranges for the "Periodo" dropdown. Anything that isn't an exact match
// for a preset (e.g. a hand-picked range, or a deep link) reads as "custom".
const PERIOD_OPTIONS = [
  { value: 'week',   label: 'Ultima settimana', days: 7 },
  { value: 'month',  label: 'Ultimo mese',      days: 30 },
  { value: 'custom', label: 'Personalizzata',   days: null },
];

function derivePeriod(since, before) {
  if (!before) {
    if (since === daysAgoIso(7))  return 'week';
    if (since === daysAgoIso(30)) return 'month';
  }
  return 'custom';
}

// v193: the filter row's look lives in components/ui/filterFields.jsx, shared with "Report Attività".
const filterLbl = FILTER_LABEL;
const fieldBase = FILTER_FIELD;
const th = {
  padding: '12px 24px', textAlign: 'left',
  fontSize: '11px', fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase',
  color: COLORS.textSecondary, fontFamily: FONT_SANS, whiteSpace: 'nowrap',
};
const td = {
  padding: '14px 24px', fontSize: '13px', color: COLORS.ink,
  fontFamily: FONT_SANS, borderTop: `1px solid ${COLORS.border}`,
};

export default function TenantRunListPage() {
  const { t: tShell } = useTranslation('shell');
  const { activeTenant } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const offset = parseInt(searchParams.get('offset') || '0', 10);
  const statusFromUrl = (searchParams.get('status') || '').split(',').filter(Boolean);
  const urlSince    = searchParams.get('since')           || daysAgoIso(7);
  const urlBefore   = searchParams.get('before')          || '';
  const urlUser     = searchParams.get('user')            || '';
  const urlPlatform = searchParams.get('platform')        || '';
  const urlFormat   = searchParams.get('content_format')  || '';

  const hasActiveFilters =
    statusFromUrl.length > 0 ||
    !!urlUser || !!urlPlatform || !!urlFormat ||
    (searchParams.get('since')  || '') !== '' ||
    (searchParams.get('before') || '') !== '';

  // Custom range where "al" (before) precedes "dal" (since). ISO yyyy-mm-dd
  // strings compare correctly lexicographically. Blocks the (empty) fetch.
  const invalidDateRange = !!urlSince && !!urlBefore && urlBefore < urlSince;

  function patchParams(patch) {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === '' || v == null) next.delete(k);
      else next.set(k, String(v));
    });
    setSearchParams(next, { replace: false });
  }

  const [runs, setRuns]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  // Row click opens the quick-look detail modal (the full /runs/:taskId page
  // still exists for deep links).
  const [openTaskId, setOpenTaskId] = useState(null);

  // Held locally (not in the URL) so picking "Personalizzata" keeps the date
  // inputs open even when the chosen range happens to equal a preset.
  const [periodMode, setPeriodMode] = useState(() => derivePeriod(urlSince, urlBefore));

  function onPeriodChange(mode) {
    setPeriodMode(mode);
    const preset = PERIOD_OPTIONS.find(o => o.value === mode);
    if (preset?.days) patchParams({ since: daysAgoIso(preset.days), before: '', offset: '0' });
    // "custom" keeps the current range and just reveals the date inputs.
  }

  const tenantId = activeTenant?.tenant_id;

  const load = useCallback(async () => {
    if (!tenantId) return;
    if (invalidDateRange) {
      // Don't fetch an impossible range — clear results and surface the message.
      setRuns([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await listRuns({
        tenant: tenantId,                                             // explicit — see plan Context
        status: statusFromUrl.length > 0 ? statusFromUrl : undefined,
        since:  urlSince  || undefined,
        before: urlBefore || undefined,
        user:   urlUser   || undefined,
        platform: urlPlatform || undefined,
        contentFormat: urlFormat || undefined,
        limit: LIMIT,
        offset,
      });
      setRuns(data.runs || []);
      setTotal(typeof data.total === 'number' ? data.total : (data.runs?.length || 0));
    } catch (err) {
      setError(err.message || 'Errore nel caricamento delle run');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, invalidDateRange, urlSince, urlBefore, urlUser, urlPlatform, urlFormat, offset, statusFromUrl.join(',')]);

  useEffect(() => { load(); }, [load]);

  if (!activeTenant) return null;

  const selectedKey = comboKey(urlPlatform, urlFormat);
  const selectValue = COMBO_OPTIONS.some(o => comboKey(o.platform, o.cf) === selectedKey) ? selectedKey : '';

  const pageCount = Math.max(1, Math.ceil(total / LIMIT));
  const current   = Math.floor(offset / LIMIT) + 1;
  const from      = total === 0 ? 0 : offset + 1;
  const to        = Math.min(offset + LIMIT, total);

  const pageBtn = (active) => ({
    width: '32px', height: '32px', borderRadius: RADII.pill,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: active ? 'none' : `1px solid ${COLORS.border}`,
    background: active ? COLORS.accent : COLORS.surface,
    color: active ? COLORS.surface : COLORS.ink,
    fontSize: '13px', fontWeight: 600, fontFamily: FONT_SANS,
    cursor: 'pointer',
  });

  return (
    <>
    {/* v193: the page header is the shared PageBand (portalled into the shell header's slot, on the
        avatar's row, like the Content Generator); the card below starts with the filters. */}
    <PageBand
      title={tShell('nav.runs')}
      subtitle="Consulta le generazioni effettuate e filtra per periodo, utente o piattaforma"
    />
    <div style={{ padding: '32px 40px', fontFamily: FONT_SANS, color: COLORS.ink, maxWidth: '1280px', margin: '0 auto' }}>
      <style>{`
        .run-row:hover { background: ${COLORS.field}; }
      `}</style>

      {error && (
        <div style={{
          marginBottom: '16px', padding: '12px 16px', borderRadius: RADII.control,
          background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.bg}`,
          color: SEMANTIC.errore.fg, fontSize: '13px',
        }}>
          {error}
        </div>
      )}

      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: RADII.section, boxShadow: SHADOW.card, overflow: 'hidden',
      }}>
        {/* Filters */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '20px', flexWrap: 'wrap', padding: '24px 24px 24px' }}>
          <div>
            <div style={filterLbl}>Periodo</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ position: 'relative' }}>
                <select
                  value={periodMode}
                  onChange={e => onPeriodChange(e.target.value)}
                  style={{ ...fieldBase, padding: '0 40px 0 14px', width: '180px', appearance: 'none', cursor: 'pointer' }}
                >
                  {PERIOD_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <ChevronDown size={18} color={COLORS.textTertiary} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              </div>

              {periodMode === 'custom' && [['since', urlSince], ['before', urlBefore]].map(([key, val]) => (
                <React.Fragment key={key}>
                  {key === 'before' && <ArrowRight size={16} color={COLORS.textTertiary} />}
                  <FilterDateField
                    value={val}
                    onChange={(v) => patchParams({ [key]: v, offset: '0' })}
                    invalid={invalidDateRange}
                  />
                </React.Fragment>
              ))}
            </div>
            {periodMode === 'custom' && invalidDateRange && (
              <div role="alert" style={{ marginTop: '8px', fontSize: '12px', color: SEMANTIC.errore.fg }}>
                La data “al” deve essere successiva alla data “dal”.
              </div>
            )}
          </div>

          <div>
            <div style={filterLbl}>Utente</div>
            <div style={{ position: 'relative' }}>
              <Mail size={16} color={COLORS.textSecondary} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder="user@example.com"
                value={urlUser}
                onChange={e => patchParams({ user: e.target.value, offset: '0' })}
                style={{ ...fieldBase, padding: '0 12px 0 40px', width: '220px' }}
              />
            </div>
          </div>

          <div>
            <div style={filterLbl}>Piattaforma / Formato</div>
            <div style={{ position: 'relative' }}>
              <select
                value={selectValue}
                onChange={e => {
                  const combo = COMBO_OPTIONS.find(o => comboKey(o.platform, o.cf) === e.target.value);
                  patchParams({ platform: combo?.platform || '', content_format: combo?.cf || '', offset: '0' });
                }}
                style={{ ...fieldBase, padding: '0 40px 0 14px', width: '190px', appearance: 'none', cursor: 'pointer' }}
              >
                <option value="">Tutti</option>
                {COMBO_OPTIONS.map(opt => (
                  <option key={comboKey(opt.platform, opt.cf)} value={comboKey(opt.platform, opt.cf)}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={18} color={COLORS.textTertiary} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              setPeriodMode('week');
              setSearchParams(new URLSearchParams(), { replace: false });
            }}
            style={FILTER_BUTTON}
          >
            Reset filtri
          </button>
        </div>

        {/* Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: COLORS.field }}>
              <th style={th}>Topic</th>
              <th style={th}>Platform / Format</th>
              <th style={th}>Stato</th>
              <th style={th}>User</th>
              <th style={th}>Creato</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td style={{ ...td, textAlign: 'center', color: COLORS.textTertiary }} colSpan={5}>Caricamento…</td>
              </tr>
            )}
            {!loading && runs.length === 0 && (
              <tr>
                <td style={{ ...td, textAlign: 'center', color: COLORS.textTertiary }} colSpan={5}>
                  {invalidDateRange
                    ? 'Correggi l’intervallo di date per vedere i risultati'
                    : hasActiveFilters
                      ? 'Nessuna generazione trovata con i filtri attuali'
                      : 'Nessuna generazione negli ultimi 7 giorni'}
                </td>
              </tr>
            )}
            {!loading && runs.map(row => (
              <tr
                key={row.task_id}
                className="run-row"
                onClick={() => setOpenTaskId(row.task_id)}
                style={{ cursor: 'pointer', transition: 'background 0.15s' }}
              >
                <td style={td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{
                      width: '32px', height: '32px', borderRadius: RADII.pill, flexShrink: 0,
                      border: `1px solid ${COLORS.border}`,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Eye size={16} color={COLORS.textSecondary} />
                    </span>
                    <span title={row.topic || ''} style={{ maxWidth: '340px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.topic || '—'}
                    </span>
                  </div>
                </td>
                <td style={{ ...td, color: COLORS.textSecondary }}>
                  {row.platform || '—'}{row.content_format ? ` / ${row.content_format}` : ''}
                </td>
                <td style={td}><RunStatusPill status={row.status} /></td>
                <td style={{ ...td, color: COLORS.textSecondary }}>
                  {row.created_by_email || row.user_email || row.user_id || '—'}
                </td>
                <td style={{ ...td, color: COLORS.textSecondary, whiteSpace: 'nowrap' }}>
                  {row.created_at
                    ? new Date(row.created_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Footer — count + numbered pagination */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '16px', padding: '16px 24px', borderTop: `1px solid ${COLORS.border}`,
        }}>
          <span style={{ fontSize: '13px', color: COLORS.textSecondary }}>
            {from}–{to} di {total} generazioni
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              aria-label="Pagina precedente"
              disabled={current <= 1}
              onClick={() => patchParams({ offset: Math.max(0, offset - LIMIT) })}
              style={{ ...pageBtn(false), opacity: current <= 1 ? 0.4 : 1, cursor: current <= 1 ? 'default' : 'pointer' }}
            >
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: pageCount }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                type="button"
                aria-current={p === current ? 'page' : undefined}
                onClick={() => patchParams({ offset: (p - 1) * LIMIT })}
                style={pageBtn(p === current)}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              aria-label="Pagina successiva"
              disabled={current >= pageCount}
              onClick={() => patchParams({ offset: offset + LIMIT })}
              style={{ ...pageBtn(false), opacity: current >= pageCount ? 0.4 : 1, cursor: current >= pageCount ? 'default' : 'pointer' }}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {openTaskId && (
        <RunDetailModal taskId={openTaskId} onClose={() => setOpenTaskId(null)} />
      )}
    </div>
    </>
  );
}
